import React from 'react';
import { ChevronRight, Loader2, Mail, TriangleAlert } from 'lucide-react';
import { Modal } from './Modal';
import { Button } from './Button';
import { Switch } from './Field';
import { BoutonTesterEmail } from './TesterEmailModal';
import { emailsApi } from '../../lib/api';

/**
 * Notifications « en lignes » (refonte des Paramètres, demande d'Emeni du 2026-10-20, sur le modèle de
 * ZenChef) : chaque email est une ligne — titre, quand il part, interrupteur — et un clic ouvre une
 * fenêtre avec ses réglages, l'aperçu de ce que reçoit le destinataire et « Tester l'email ».
 *
 * Ce mode n'est activé que là où un écran le demande (onglet Paramètres > Notifications) : les fenêtres
 * de réglage des modules (Factures, Fournisseurs…) gardent le bloc complet.
 *
 * Les valeurs restent tenues par l'écran parent, comme avant : on ne change ni ce qui est enregistré, ni
 * la façon de l'enregistrer. L'écran fournit seulement `enregistrer`, appelé quand on bascule une ligne
 * ou qu'on valide la fenêtre.
 */
export interface ModeNotifications {
    enLignes: boolean;
    /** Demande l'enregistrement de l'écran (après que les nouvelles valeurs sont posées). */
    enregistrer?: () => void;
}

export const ModeNotificationsContext = React.createContext<ModeNotifications>({ enLignes: false });

/**
 * Enregistrement « au prochain rendu » : la ligne change une valeur puis demande l'enregistrement dans
 * la même foulée. Appeler la sauvegarde tout de suite lirait l'ancienne valeur ; on la lance donc juste
 * après le rendu qui porte la nouvelle.
 */
export function useEnregistrementDiffere(enregistrer: () => unknown) {
    const [demandes, setDemandes] = React.useState(0);
    const ref = React.useRef(enregistrer);
    ref.current = enregistrer;
    React.useEffect(() => {
        if (demandes > 0) ref.current();
    }, [demandes]);
    return React.useCallback(() => setDemandes((n) => n + 1), []);
}

/** Fournit le mode en lignes à un écran de notifications, avec SA fonction d'enregistrement. */
export function FournisseurEnregistrement({ enregistrer, children }: { enregistrer: () => unknown; children: React.ReactNode }) {
    const parent = React.useContext(ModeNotificationsContext);
    const demander = useEnregistrementDiffere(enregistrer);
    const valeur = React.useMemo(() => ({ ...parent, enregistrer: demander }), [parent, demander]);
    return <ModeNotificationsContext.Provider value={valeur}>{children}</ModeNotificationsContext.Provider>;
}

/**
 * Un groupe d'emails (Rendez-vous, Devis…). En lignes : une carte, une ligne par email. Ailleurs : le
 * titre de section, puis les blocs complets comme avant.
 */
export function GroupeNotifications({ titre, icone, children }: { titre: string; icone?: React.ReactNode; children: React.ReactNode }) {
    const { enLignes } = React.useContext(ModeNotificationsContext);
    if (enLignes) {
        return (
            <section>
                <h2 className="text-[11px] font-bold uppercase tracking-[.09em] text-content-muted mb-2 flex items-center gap-1.5">{icone}{titre}</h2>
                <div className="rounded-2xl border border-border overflow-hidden divide-y divide-border">{children}</div>
            </section>
        );
    }
    return (
        <section className="space-y-4">
            <h2 className="text-xs font-black uppercase tracking-wider text-content-muted flex items-center gap-1.5">{icone}{titre}</h2>
            {children}
        </section>
    );
}

/** Petit état d'enregistrement en bas d'écran : il remplace le gros bouton « Enregistrer ». */
export function EtatEnregistrement({ etat }: { etat: 'repos' | 'en_cours' | 'fait' | 'echec' }) {
    if (etat === 'repos') return null;
    const styles = {
        en_cours: 'bg-surface text-content-muted border-border',
        fait: 'bg-state-ok-soft text-state-ok border-transparent',
        echec: 'bg-state-danger-soft text-state-danger border-transparent',
    }[etat];
    return (
        <div role="status" aria-live="polite"
            className={`fixed bottom-6 right-6 z-[80] flex items-center gap-2 rounded-full border px-4 py-2 text-[12.5px] font-semibold shadow-lg ${styles}`}>
            {etat === 'en_cours' && <><Loader2 size={13} className="animate-spin" /> Enregistrement…</>}
            {etat === 'fait' && <>Enregistré ✓</>}
            {etat === 'echec' && <><TriangleAlert size={13} /> L'enregistrement a échoué, réessayez.</>}
        </div>
    );
}

/** L'email est mis en page pour 600 px ; l'aperçu est plus étroit. Comme un client mail sur téléphone,
 *  on laisse les tableaux se resserrer au lieu d'afficher une barre de défilement horizontale. */
const ajusterALaLargeur = (html: string) => html.replace('</head>',
    '<style>html,body{min-width:0!important;overflow-x:hidden}table{max-width:100%!important}img{max-width:100%;height:auto}</style></head>');

/** L'email tel que le destinataire le verra, recalculé pendant qu'on écrit. */
export function ApercuEmail({ sujet, corps }: { sujet: string; corps: string }) {
    const [rendu, setRendu] = React.useState<{ sujet: string; html: string } | null>(null);
    const [erreur, setErreur] = React.useState('');
    const [charge, setCharge] = React.useState(true);

    React.useEffect(() => {
        let annule = false;
        setCharge(true);
        // Attendre une courte pause dans la frappe : pas une requête par lettre.
        const minuteur = window.setTimeout(async () => {
            try {
                const r = await emailsApi.apercu({ subject: sujet, body: corps });
                if (!annule) { setRendu(r); setErreur(''); }
            } catch (e: any) {
                if (!annule) setErreur(e?.message || "L'aperçu n'a pas pu être préparé.");
            } finally {
                if (!annule) setCharge(false);
            }
        }, 450);
        return () => { annule = true; window.clearTimeout(minuteur); };
    }, [sujet, corps]);

    return (
        <div className="flex flex-col h-full min-h-[420px]">
            <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[11px] font-bold uppercase tracking-[.09em] text-content-muted">Aperçu</span>
                {charge && <Loader2 size={13} className="animate-spin text-content-muted" aria-label="Mise à jour de l'aperçu" />}
            </div>
            <div className="rounded-xl border border-border overflow-hidden flex flex-col flex-1 bg-surface-soft">
                <div className="px-3 py-2 border-b border-border bg-surface text-[12.5px] text-content flex items-center gap-2 min-w-0">
                    <Mail size={13} className="text-content-muted shrink-0" />
                    <span className="truncate font-semibold">{rendu?.sujet || sujet || '(sans objet)'}</span>
                </div>
                {erreur ? (
                    <p className="p-4 text-[12.5px] text-state-danger">{erreur}</p>
                ) : rendu ? (
                    // Aucun script dans le cadre (pas de allow-scripts) : il affiche, il n'exécute rien.
                    // allow-same-origin est nécessaire : avec un sandbox vide, l'en-tête
                    // « frame-ancestors 'self' » du site (public/_headers) empêche le rendu, le cadre reste blanc.
                    <iframe title="Aperçu de l'email" sandbox="allow-same-origin" srcDoc={ajusterALaLargeur(rendu.html)}
                        className="w-full flex-1 min-h-[380px] bg-white border-0" />
                ) : (
                    <div className="flex-1 flex items-center justify-center"><Loader2 size={20} className="animate-spin text-content-muted" /></div>
                )}
            </div>
            <p className="text-[11px] text-content-muted mt-2">Les variables sont remplacées par des exemples. Le reste est identique à ce qui part.</p>
        </div>
    );
}

/**
 * Une notification en une ligne, et sa fenêtre de réglage.
 * `editeur` est le formulaire existant (objet, texte, variables, pièce jointe) : il est déplacé dans la
 * fenêtre, pas réécrit. « Annuler » remet les valeurs d'avant l'ouverture.
 */
export function LigneNotification({
    titre, description, toggle, toujours, sujet, corps, setSujet, setCorps, piece, setPiece, editeur, extra, variables,
}: {
    /** Panneau des variables : placé à droite, à côté du texte, pour qu'on voie où elles arrivent. */
    variables?: React.ReactNode;
    titre: string;
    description?: React.ReactNode;
    toggle?: { checked: boolean; onChange: (v: boolean) => void };
    /** Sans interrupteur mais envoyé automatiquement (obligation légale) : « Toujours » au lieu de « Sur clic ». */
    toujours?: boolean;
    sujet: string;
    corps: string;
    setSujet: (v: string) => void;
    setCorps: (v: string) => void;
    piece?: unknown;
    setPiece?: (v: any) => void;
    editeur: React.ReactNode;
    extra?: React.ReactNode;
}) {
    const { enregistrer } = React.useContext(ModeNotificationsContext);
    const [ouvert, setOuvert] = React.useState(false);
    const [volet, setVolet] = React.useState<'apercu' | 'variables'>('apercu');
    const avant = React.useRef<{ sujet: string; corps: string; piece: unknown; actif?: boolean } | null>(null);

    const ouvrir = () => {
        avant.current = { sujet, corps, piece, actif: toggle?.checked };
        setOuvert(true);
    };
    const annuler = () => {
        const a = avant.current;
        if (a) {
            setSujet(a.sujet);
            setCorps(a.corps);
            if (setPiece) setPiece(a.piece ?? null);
            if (toggle && a.actif !== undefined && a.actif !== toggle.checked) toggle.onChange(a.actif);
        }
        setOuvert(false);
    };
    const valider = () => {
        enregistrer?.();
        setOuvert(false);
    };
    const basculer = (v: boolean) => {
        toggle?.onChange(v);
        enregistrer?.();
    };

    const actif = toggle ? toggle.checked : true;

    return (
        <>
            <div className="flex items-center gap-3 px-4 py-3 bg-surface hover:bg-surface-soft transition-colors">
                <button type="button" onClick={ouvrir}
                    className="flex-1 min-w-0 flex items-center gap-3 text-left outline-none focus-visible:ring-2 focus-visible:ring-brand-500 rounded-lg -my-1 py-1">
                    <span className="min-w-0 flex-1">
                        <span className={`block text-[13.5px] font-semibold leading-snug ${actif ? 'text-content' : 'text-content-muted'}`}>{titre}</span>
                        {description && <span className="block text-[12px] text-content-muted leading-snug mt-0.5 line-clamp-2">{description}</span>}
                    </span>
                    <ChevronRight size={16} className="text-content-muted shrink-0" />
                </button>
                {toggle ? (
                    // Le Switch sans libellé n'est qu'un <span> : le <label> autour rend tout l'interrupteur cliquable.
                    <label className="shrink-0 cursor-pointer flex">
                        <Switch checked={toggle.checked} onChange={(e) => basculer(e.target.checked)}
                            aria-label={`${toggle.checked ? 'Désactiver' : 'Activer'} : ${titre}`} />
                    </label>
                ) : toujours ? (
                    <span className="text-[10.5px] font-semibold text-content-muted shrink-0 rounded-full border border-border px-2 py-0.5 whitespace-nowrap"
                        title="Envoyé automatiquement à chaque fois : il ne peut pas être désactivé">Toujours</span>
                ) : (
                    // Sans interrupteur : l'email part au moment où vous le déclenchez (bouton dédié).
                    <span className="text-[10.5px] font-semibold text-content-muted shrink-0 rounded-full border border-border px-2 py-0.5 whitespace-nowrap"
                        title="Envoyé seulement quand vous cliquez sur le bouton correspondant">Sur clic</span>
                )}
            </div>

            <Modal open={ouvert} onClose={annuler} widthPx={1100} title={titre}
                subtitle={description} icon={<Mail size={16} />} footerLayout="split"
                footer={
                    <>
                        <BoutonTesterEmail sujet={sujet} corps={corps} nomModele={titre} />
                        <div className="flex gap-2">
                            <Button variant="outline" onClick={annuler}>Annuler</Button>
                            <Button onClick={valider}>Enregistrer</Button>
                        </div>
                    </>
                }>
                <div className="grid gap-5 lg:grid-cols-2">
                    <div className="space-y-3 min-w-0">
                        {toggle && (
                            <Switch checked={toggle.checked} onChange={(e) => toggle.onChange(e.target.checked)}
                                label={toggle.checked ? 'Envoi activé' : 'Envoi désactivé'} />
                        )}
                        {extra}
                        {editeur}
                    </div>
                    <div className="min-w-0 lg:sticky lg:top-0 lg:self-start space-y-2">
                        {variables && (
                            <div className="inline-flex rounded-[10px] border border-border bg-surface-soft p-0.5" role="group" aria-label="Panneau de droite">
                                {([['apercu', 'Aperçu'], ['variables', 'Variables']] as const).map(([cle, nom]) => (
                                    <button key={cle} type="button" aria-pressed={volet === cle} onClick={() => setVolet(cle)}
                                        className={`px-3.5 py-1 rounded-lg text-[12.5px] font-semibold ${volet === cle ? 'bg-surface text-content shadow-sm' : 'text-content-muted'}`}>
                                        {nom}
                                    </button>
                                ))}
                            </div>
                        )}
                        {/* Les deux restent montés : repasser sur « Aperçu » ne relance pas le calcul du rendu. */}
                        <div className={variables && volet === 'variables' ? 'hidden' : ''}>
                            <ApercuEmail sujet={sujet} corps={corps} />
                        </div>
                        {variables && <div className={volet === 'variables' ? '' : 'hidden'}>{variables}</div>}
                    </div>
                </div>
            </Modal>
        </>
    );
}

/**
 * Le corps d'une fenêtre de réglages d'emails d'un module (Factures, Devis, Congés…) : une carte, une
 * ligne par email, chaque ligne s'enregistre toute seule. Même présentation que Paramètres > Notifications.
 * Le module garde sa fonction d'enregistrement et ses états : on ne change que la façon de les montrer.
 */
export function ListeEmailsModule({ enregistrer, etat, children }: {
    enregistrer: () => unknown;
    etat: 'repos' | 'en_cours' | 'fait' | 'echec';
    children: React.ReactNode;
}) {
    const valeur = React.useMemo(() => ({ enLignes: true }), []);
    return (
        <ModeNotificationsContext.Provider value={valeur}>
            <FournisseurEnregistrement enregistrer={enregistrer}>
                <div className="rounded-2xl border border-border overflow-hidden divide-y divide-border">{children}</div>
                <EtatEnregistrement etat={etat} />
            </FournisseurEnregistrement>
        </ModeNotificationsContext.Provider>
    );
}

/**
 * La fenêtre complète des réglages d'emails d'un module : titre, liste des emails en lignes, « Fermer ».
 * Chaque module n'y met que ses `NotificationBlock` ; l'enregistrement, lui, reste le sien.
 */
export function FenetreEmailsModule({ titre, sousTitre, icone, onClose, chargement, erreur, enregistrer, etat, avant, embarque, children }: {
    titre: string;
    sousTitre?: React.ReactNode;
    icone: React.ReactNode;
    onClose: () => void;
    chargement: boolean;
    erreur?: string;
    enregistrer: () => unknown;
    etat: 'repos' | 'en_cours' | 'fait' | 'echec';
    /** Au-dessus de la liste (onglets, filtre…). */
    avant?: React.ReactNode;
    /** Dans l'onglet d'une autre fenêtre de réglages : le corps seul, sans cadre ni « Fermer ». */
    embarque?: boolean;
    children: React.ReactNode;
}) {
    const corps = chargement ? (
        <div className="flex justify-center py-12"><Loader2 className="animate-spin text-content-muted" size={28} /></div>
    ) : (
        <div className="space-y-3">
            {avant}
            <ListeEmailsModule enregistrer={enregistrer} etat={etat}>{children}</ListeEmailsModule>
            {erreur && <p className="text-[12px] font-semibold text-state-danger">{erreur}</p>}
        </div>
    );
    if (embarque) return corps;
    return (
        <Modal open onClose={onClose} size="lg" icon={icone} title={titre}
            subtitle={sousTitre ?? "Un clic sur un titre ouvre son texte et l'aperçu ; l'interrupteur active ou coupe l'email."}
            footer={<Button variant="outline" onClick={onClose}>Fermer</Button>}>
            {corps}
        </Modal>
    );
}
